// 타격 방사선(2026-10-27 전투 액션 VFX, docs/ACTION-VFX-REF.md 2절 스파크): 맞은 자리에서 흰 별 줄기 6~10개가 0.1초 남짓 뻗었다 사라진다.
// 카메라를 향한 평면에 그린다(줄기마다 마름모: 안쪽 끝 0 → 30% 지점 최대 폭 → 바깥 끝 0). 흰 심 + 고유색 가장자리, 가산 혼합.
// 화면 전체 섬광이 아니라 국소 효과다(섬광 예산과 별개). 판정 순간당 상한은 ActionVfx가 정한다.
// 쓰는 칸: length = 줄기 길이(m), width = 줄기 폭(m), variant = 줄기 수(최대 HIT_RAYS_MAX), seed = 각도 위상, life·color·color2·intensity. 드로우콜 1.
import * as THREE from 'three/webgpu';
import { abs, float, mix, uv } from 'three/tsl';
import { BatchModule, fxAttributes, type BatchLayout, type Slot } from '../modules/batch';
import { fxMaterial } from '../modules/material';
import { clamp01, easeOut } from '../modules/path';
import type { ActionModuleKind } from '../modules/types';

export const HIT_RAYS_MAX = 10;
export const HIT_BURST_CAP = 12;
/** 나이 0의 줄기 길이 비율(타격 순간·히트스톱 정지 프레임에 보이는 크기) */
export const HIT_GROW0 = 0.45;
/** 마름모 최대 폭 지점(길이 비율) */
const WAIST = 0.3;

function burstLayout(): BatchLayout {
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let r = 0; r < HIT_RAYS_MAX; r++) {
    // 정점: 안쪽 끝, 허리 왼쪽, 허리 오른쪽, 바깥 끝
    uvs.push(0, 0.5, WAIST, 0, WAIST, 1, 1, 0.5);
    const b = r * 4;
    indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
  }
  return { verts: HIT_RAYS_MAX * 4, indices, uv: uvs };
}

function makeMaterial(): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const env = ctl.y;
  const u = uv().x, v = uv().y;
  const core = float(1).sub(abs(v.mul(2).sub(1))).pow(1.5);
  // 흰 심(color2)이 가운데, 가장자리는 고유색. 바깥 끝으로 갈수록 옅게
  const col = mix(t1.rgb, t2.rgb, core).mul(t1.w);
  const alpha = env.mul(float(1).sub(u.mul(0.55)));
  return fxMaterial(col, alpha);
}

/** 줄기마다 길이·각도 흔들림(0..1) */
function hash01(i: number, seed: number): number {
  const x = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export class HitBurstModule extends BatchModule {
  readonly kind: ActionModuleKind = 'hitBurst';
  /** 카메라 오른쪽·위 방향(월드). 렌더 프레임마다 setCamera로 갱신 */
  private rx = 1; private ry = 0; private rz = 0;
  private ux = 0; private uy = 1; private uz = 0;

  constructor(cap = HIT_BURST_CAP) {
    super(cap, burstLayout(), makeMaterial(), 24, 'fx-hit-burst');
  }

  setCamera(camera: THREE.Camera): void {
    const e = camera.matrixWorld.elements;
    this.rx = e[0]; this.ry = e[1]; this.rz = e[2];
    this.ux = e[4]; this.uy = e[5]; this.uz = e[6];
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const t = clamp01(s.age / s.life);
    // 나이 0에서도 45% 길이로 보인다(히트스톱 동안 영웅 시계가 멈춰 나이 0인 채로 그려진다 — Codex P2). 처음 35%에 다 자라고,
    // 끝으로 갈수록 바깥으로 밀려나며 가늘어진다
    const grow = HIT_GROW0 + (1 - HIT_GROW0) * easeOut(clamp01(t / 0.35));
    const n = Math.min(HIT_RAYS_MAX, Math.max(0, s.variant | 0));
    const L = s.length * s.scale, W = s.width * s.scale * (1 - 0.6 * t);
    for (let r = 0; r < HIT_RAYS_MAX; r++) {
      const k = o + r * 12;
      if (r >= n) {
        for (let q = 0; q < 12; q++) out[k + q] = q % 3 === 0 ? s.x : q % 3 === 1 ? s.y : s.z;
        continue;
      }
      const h = hash01(r, s.seed);
      const ang = s.seed * 6.283 + (r / n) * 6.283 + (h - 0.5) * 0.5;
      // 긴 줄기·짧은 줄기 번갈아(별 모양)
      const len = L * ((r & 1) === 0 ? 1 : 0.55) * (0.8 + 0.4 * h) * grow;
      const r0 = len * 0.12 + L * 0.25 * t;
      const r1 = r0 + len;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      // 방향 = 오른쪽·cos + 위·sin, 옆 = 오른쪽·(-sin) + 위·cos
      const dx = this.rx * ca + this.ux * sa, dy = this.ry * ca + this.uy * sa, dz = this.rz * ca + this.uz * sa;
      const px = -this.rx * sa + this.ux * ca, py = -this.ry * sa + this.uy * ca, pz = -this.rz * sa + this.uz * ca;
      const rw = r0 + (r1 - r0) * 0.3, hw = W * 0.5 * ((r & 1) === 0 ? 1 : 0.7);
      out[k] = s.x + dx * r0; out[k + 1] = s.y + dy * r0; out[k + 2] = s.z + dz * r0;
      out[k + 3] = s.x + dx * rw - px * hw; out[k + 4] = s.y + dy * rw - py * hw; out[k + 5] = s.z + dz * rw - pz * hw;
      out[k + 6] = s.x + dx * rw + px * hw; out[k + 7] = s.y + dy * rw + py * hw; out[k + 8] = s.z + dz * rw + pz * hw;
      out[k + 9] = s.x + dx * r1; out[k + 10] = s.y + dy * r1; out[k + 11] = s.z + dz * r1;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const t = clamp01(s.age / s.life);
    out[0] = t;
    out[1] = Math.pow(1 - t, 1.4);
    out[2] = s.seed;
    out[3] = 0;
  }
}
